import React, { useEffect, useState } from "react";
import {
  fetchAddress,
  addAddress,
  mutateAddress,
} from "../../../node_modules/shops-query/src/modules/address/index.js";
import { HiOutlinePencilSquare } from "react-icons/hi2";
import { FiTrash } from "react-icons/fi";
import AddAddressDialogBox from "./addAddressDialogBox";
import ConfirmationDialog from "../confirmationModal";
import { getUserId, shopid } from "../../utils/url";
import { getUserData } from "../../utils/url";
import UpdateProfileDialogBox from "./updateProfileDialogBox";
import Loader from "../loading/index";

const MyProfile = () => {
  const [userInfo, setUserInfo] = useState([]);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [userName, setUserName] = useState([]);
  const [editUserName, setEditUserName] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [editAddress, setEditAddress] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userCookie, setUserCookie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [deletingAddress, setDeletingAddress] = useState(false);

  useEffect(() => {
    const getUserData = async () => {
      try {
        const userData = await getUserId();
        setUserId(userData);
      } catch (error) {
        console.log("Something went wrong while fetching user id", error);
      }
    };
    getUserData();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const data = getUserData();
    setUserCookie(data);
  }, []);

  useEffect(() => {
    if (!userId) return;
    const getUserData = async () => {
      try {
        setLoadingAddress(true);
        const userData = await fetchAddress({ userId: Number(userId) });
        setUserInfo(userData);
      } catch (error) {
        console.log("Something went wrong", error);
      } finally {
        setLoadingAddress(false);
        setLoading(false);
      }
    };
    getUserData();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const getUserProfileInfo = async () => {
      try {
        const data = await fetchAddress({ userId: Number(userId) });
        setUserName(data);
      } catch (error) {
        console.log("Something went wrong", error);
      }
    };
    getUserProfileInfo();
  }, [userId]);

  const handleAddDialogBox = () => {
    setEditAddress(null);
    setAddressDialogOpen(true);
  };

  const handleDeleteAddress = async () => {
    try {
      setDeletingAddress(true);
      await mutateAddress({
        userId: Number(userId),
        addressId: addressToDelete,
        delete: true,
      });
      setUserInfo((prev) => prev.filter((addr) => addr.id !== addressToDelete));
      setDeleteConfirmation(false);
      setAddressToDelete(null);
    } catch (error) {
      console.log("Something went wrong while deleting ", error);
    } finally {
      setDeletingAddress(false);
    }
  };

  const saveAddressHandler = async (addressData, addressId = null) => {
    try {
      const payload = {
        ...addressData,
        name: addressData.firstName,
      };

      if (addressId) {
        await mutateAddress({
          ...payload,
          userId: Number(userId),
          addressId,
          update: true,
        });
      } else {
        await addAddress(payload);
      }

      const refreshed = await fetchAddress({ userId: Number(userId) });
      setUserInfo(refreshed);
      setEditAddress(null);
      setAddressDialogOpen(false); // Close modal after successful save
    } catch (error) {
      console.log("Something went wrong while saving:", error);
      throw error; // Re-throw to handle in child component
    }
  };

  const handleEditAddress = (address) => {
    setEditAddress(address);
    setAddressDialogOpen(true);
  };

  const handleCloseAddressDialog = () => {
    setAddressDialogOpen(false);
    setEditAddress(null);
  };

  // Show main loading while initial data is loading
  if (loading) {
    return (
      <div className="w-full h-[500px] flex items-center justify-center font-inter">
        <Loader color="#10B981" size="xl" text="Loading your profile..." />
      </div>
    );
  }

  return (
    <div className="font-poppins md:p-0 p-4 sm:ml-[40px] md:ml-[70px] md:mb-32">
      {/* Header */}
      <p className="font-semibold text-[20px] sm:text-[22px] md:text-[24px] leading-[100%] tracking-[0%] uppercase mt-[24px] sm:mt-[32px] text-[#101011]">
        My Profile
      </p>

      {/* Personal Details */}
      <div className="mt-[24px] sm:mt-[32px] flex flex-col sm:flex-row sm:items-center">
        <p className="font-semibold text-[16px] sm:text-[18px] leading-[100%] capitalize text-[#424242] whitespace-nowrap">
          Personal Details
        </p>
        <div className="hidden sm:block ml-[14px] w-full h-[1px] bg-[#D5D5D5] mr-[70px]" />
      </div>

      {/* Name */}
      <div className="flex items-center gap-2 mt-[20px] flex-wrap">
        <p className="font-semibold text-[20px] sm:text-[22px] md:text-[24px] leading-[100%] uppercase text-[#101011]">
          {userName?.[0]?.name}
        </p>
      </div>
      <div className="flex md:flex-row flex-col md:space-x-4">
        {/* Email & Mobile */}
        <p className="mt-[12px] text-[14px] sm:text-[16px]">
          Email : {userCookie?.email || ""}
        </p>

        <p className="mt-[12px] text-[14px] sm:text-[16px]">
          Mobile Number : {userCookie?.mobile}
        </p>
      </div>
      {/* Address Header */}
      <div className="flex flex-col sm:flex-row sm:items-center mt-[40px] sm:mt-[55px]">
        <div className="flex items-center">
          <p className="font-semibold text-[16px] sm:text-[18px] leading-[100%] capitalize text-[#424242]">
            Address
          </p>

          <p
            className="font-semibold text-[16px] sm:text-[18px] leading-[100%] uppercase underline text-primary-default ml-4 sm:ml-[24px] cursor-pointer whitespace-nowrap"
            onClick={() => handleAddDialogBox()}
          >
            + ADD new address
          </p>
        </div>

        {/* FIXED — FLEXIBLE DIVIDER */}
        <div className="hidden sm:block flex-1 h-[1px] bg-[#D5D5D5] ml-6 mr-[70px]" />

        {addressDialogOpen && (
          <AddAddressDialogBox
            isOpen={addressDialogOpen}
            onClose={handleCloseAddressDialog}
            onSave={saveAddressHandler}
            initialData={editAddress}
            userId={userId}
          />
        )}
      </div>

      {/* Address Cards */}
      <div
        className="
          mt-[20px]
          grid grid-cols-1
          sm:grid-cols-2
          lg:grid-cols-3
          gap-y-[24px]
          gap-x-[24px] md:mt-12
        "
      >
        {userInfo?.map((address, index) => (
          <div
            key={index}
            className="border border-[#E5E5E5] sm:border-none rounded-[8px] p-[16px] sm:p-0"
          >
            {/* FIXED — Name + icons (no overflow on zoom) */}
            <div className="flex items-center flex-wrap gap-3">
              <p className="font-semibold text-[18px] leading-[100%] capitalize text-[#101011] whitespace-nowrap">
                {address?.name}
              </p>

              <HiOutlinePencilSquare
                className="h-[20px] sm:h-[24px] w-[20px] sm:w-[24px] cursor-pointer"
                onClick={() => handleEditAddress(address)}
              />

              <button
                onClick={() => {
                  setDeleteConfirmation(true);
                  setAddressToDelete(address.id);
                }}
              >
                <FiTrash className="w-[18px] sm:w-[20px] h-[18px] sm:h-[21px] cursor-pointer" />
              </button>
            </div>

            {/* Address details */}
            <p className="font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] text-[#424242] capitalize mt-[8px]">
              {address?.street} <br />
              {address?.city} {address?.pincode}, <br />
              {address?.state}, {address?.country}. <br />
              {address?.phone}
            </p>
          </div>
        ))}
      </div>

      <ConfirmationDialog
        isOpen={deleteConfirmation}
        title="Are You Sure You Want To Delete the Address?"
        confirmLabel="DELETE"
        cancelLabel="CANCEL"
        onConfirm={handleDeleteAddress}
        onCancel={() => setDeleteConfirmation(false)}
      />

      {editUserName && <UpdateProfileDialogBox />}
    </div>
  );
};

export default MyProfile;